import { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Breadcrumb from "@/components/feature/Breadcrumb";
import PageSEO from "@/components/feature/PageSEO";
import { testimonials } from "@/mocks/testimonials";
import { siteInfo } from "@/mocks/siteInfo";

export default function About() {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({ name: "", email: "", subject: "", message: "" });
  const [formStatus, setFormStatus] = useState<"success" | "error" | "">("");
  const [formMsg, setFormMsg] = useState("");

  const breadcrumbItems = [{ label: t("nav.about") }];

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypot = form.querySelector<HTMLInputElement>('input[name="website_alt"]');
    if (honeypot && honeypot.value.trim() !== "") {
      setFormMsg("提交成功！我们会尽快与您联系。");
      setFormStatus("success");
      return;
    }
    setFormMsg("提交成功！我们会尽快与您联系。");
    setFormStatus("success");
    setFormData({ name: "", email: "", subject: "", message: "" });
  };

  const schemas = [
    {
      "@context": "https://schema.org",
      "@type": "AboutPage",
      name: "关于我们 - " + siteInfo.name,
      description: siteInfo.description,
      url: siteInfo.url + "/about",
      mainEntity: {
        "@type": "Organization",
        name: siteInfo.name,
        url: siteInfo.url,
        description: siteInfo.description,
        foundingDate: siteInfo.foundingYear,
        contactPoint: {
          "@type": "ContactPoint",
          telephone: "+86-400-888-0000",
          contactType: "customer service",
          availableLanguage: ["Chinese"],
          email: siteInfo.email,
        },
        address: {
          "@type": "PostalAddress",
          streetAddress: "望京SOHO T3-A 2501",
          addressLocality: "北京市朝阳区",
          addressCountry: "CN",
        },
      },
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: t("breadcrumb.home"), item: siteInfo.url },
        { "@type": "ListItem", position: 2, name: t("nav.about") },
      ],
    },
  ];

  return (
    <>
      <PageSEO
        title={"关于我们 - " + siteInfo.name}
        description="了解在线漫画韩漫的品牌故事、发展历程、平台数据与商务合作联系方式。我们致力于为韩漫爱好者提供最优质的免费阅读体验。"
        keywords="在线漫画韩漫,关于我们,韩漫平台介绍,商务合作,联系方式,漫画平台品牌"
        canonicalUrl={siteInfo.url + "/about"}
      />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schemas) }} />
      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-16">
          <Breadcrumb items={breadcrumbItems} />

          <section className="relative w-full min-h-[400px] md:min-h-[500px] flex items-center justify-center overflow-hidden">
            <div className="absolute inset-0">
              <img
                src="https://readdy.ai/api/search-image?query=Modern%20creative%20office%20workspace%20with%20warm%20ambient%20lighting%2C%20comic%20art%20on%20walls%2C%20bookshelves%20with%20colorful%20graphic%20novels%2C%20stylish%20industrial%20design%20interior%2C%20soft%20natural%20light%20from%20windows%2C%20editorial%20photography%20style%2C%20warm%20neutral%20tones&width=1920&height=1080&seq=about-hero-bg&orientation=landscape"
                alt="在线漫画韩漫 - 关于我们"
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/40 to-black/60"></div>
            </div>
            <div className="relative z-10 w-full max-w-3xl mx-auto px-4 md:px-6 text-center">
              <h1 className="text-3xl md:text-5xl font-bold text-white leading-tight">
                关于我们
              </h1>
              <p className="mt-4 text-base md:text-lg text-gray-200 max-w-xl mx-auto leading-relaxed">
                成立于2021年，我们致力于打造全球领先的韩漫免费阅读平台，为亿万漫迷带来最纯粹的阅读体验。
              </p>
            </div>
          </section>

          <section className="px-4 md:px-6 py-14 md:py-20">
            <div className="max-w-7xl mx-auto">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16">
                <div>
                  <h2 className="text-2xl md:text-3xl font-bold text-foreground-950">
                    品牌故事
                  </h2>
                  <div className="mt-6 space-y-4 text-sm md:text-base text-foreground-700 leading-relaxed">
                    <p>
                      <strong>在线漫画韩漫</strong>成立于2021年，创始团队是一群热爱韩国漫画的资深漫迷。我们深刻理解读者在寻找优质韩漫时面临的困难：资源分散、翻译质量参差不齐、更新不及时。
                    </p>
                    <p>
                      怀着"让每一位漫迷都能无障碍享受韩漫之美"的初心，我们建立了这个平台。经过数年发展，如今已成为国内领先的韩漫聚合阅读平台之一，日活用户超过50万，覆盖作品超过10,000部。
                    </p>
                    <p>
                      我们的愿景是成为全球最值得信赖的韩漫阅读平台，通过持续的技术创新和服务优化，为每一位用户打造沉浸式的漫画阅读体验。
                    </p>
                  </div>
                  <div className="mt-8 flex items-center gap-8">
                    <div>
                      <p className="text-3xl md:text-4xl font-bold text-foreground-950">10,000+</p>
                      <p className="text-sm text-foreground-500 mt-1">覆盖作品</p>
                    </div>
                    <div>
                      <p className="text-3xl md:text-4xl font-bold text-foreground-950">500万+</p>
                      <p className="text-sm text-foreground-500 mt-1">全球用户</p>
                    </div>
                  </div>
                </div>
                <div className="relative rounded-[24px] overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=Diverse%20team%20of%20creative%20professionals%20collaborating%20in%20modern%20studio%20workspace%2C%20warm%20natural%20lighting%2C%20colorful%20comic%20art%20boards%20on%20walls%2C%20friendly%20atmosphere%2C%20editorial%20photography%2C%20casual%20business%20culture%2C%20warm%20neutral%20color%20palette&width=800&height=600&seq=about-team&orientation=landscape"
                    alt="在线漫画韩漫团队"
                    className="w-full h-full object-cover object-top"
                  />
                  <div className="absolute top-4 left-4">
                    <span className="px-3 py-1.5 rounded-full bg-white/90 backdrop-blur-sm text-xs font-medium text-foreground-800">
                      成立于 2021
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section className="px-4 md:px-6 py-14 md:py-20 bg-background-100">
            <div className="max-w-7xl mx-auto">
              <div className="text-center mb-12">
                <p className="text-sm font-semibold uppercase tracking-wider text-foreground-400">
                  核心数据
                </p>
                <h2 className="mt-1 text-2xl md:text-3xl font-bold text-foreground-950">
                  平台实力一览
                </h2>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                {[
                  { icon: "ri-book-line", value: "10,000+", label: "收录作品", desc: "覆盖全部主流韩漫" },
                  { icon: "ri-user-3-line", value: "500万+", label: "全球用户", desc: "覆盖全球200+国家" },
                  { icon: "ri-global-line", value: "15种", label: "支持语言", desc: "AI智能多语言翻译" },
                  { icon: "ri-time-line", value: "200+", label: "日更新量", desc: "全年365天不间断" },
                ].map((stat) => (
                  <div key={stat.label} className="bg-white rounded-2xl p-6 text-center border border-background-200">
                    <div className="w-12 h-12 mx-auto flex items-center justify-center rounded-xl bg-primary-100 text-primary-600 mb-4">
                      <i className={stat.icon + " text-xl"}></i>
                    </div>
                    <p className="text-2xl md:text-3xl font-bold text-foreground-950">{stat.value}</p>
                    <p className="mt-1 text-sm font-medium text-foreground-800">{stat.label}</p>
                    <p className="mt-1 text-xs text-foreground-500">{stat.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <section className="px-4 md:px-6 py-14 md:py-20">
            <div className="max-w-7xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="text-2xl md:text-3xl font-bold text-foreground-950">
                  用户口碑
                </h2>
                <p className="mt-3 text-sm text-foreground-600">来自全球用户的真实评价</p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {testimonials.map((t) => (
                  <div
                    key={t.id}
                    className="bg-white rounded-2xl p-6 border border-background-200"
                  >
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-10 h-10 rounded-full overflow-hidden flex-shrink-0">
                        <img
                          src={t.avatarUrl}
                          alt={t.name}
                          className="w-full h-full object-cover object-top"
                        />
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-foreground-900">{t.name}</p>
                        <p className="text-xs text-foreground-500">{t.role}</p>
                      </div>
                    </div>
                    <p className="text-sm text-foreground-700 leading-relaxed">{t.quote}</p>
                    <div className="mt-3 flex gap-0.5">
                      {[1, 2, 3, 4, 5].map((s) => (
                        <i key={s} className="ri-star-fill text-amber-400 text-sm w-4 h-4 flex items-center justify-center"></i>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <section className="px-4 md:px-6 py-14 md:py-20 bg-background-100">
            <div className="max-w-7xl mx-auto">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
                <div>
                  <h2 className="text-2xl md:text-3xl font-bold text-foreground-950">
                    联系我们
                  </h2>
                  <p className="mt-4 text-sm text-foreground-600 leading-relaxed">
                    无论是商务合作、内容授权还是用户反馈，我们都非常期待与您交流。请通过以下方式联系我们的团队。
                  </p>

                  <div className="mt-8 space-y-5">
                    {[
                      { icon: "ri-phone-line", label: "商务合作热线", value: "400-888-0000" },
                      { icon: "ri-mail-line", label: "邮箱地址", value: "hello@xinxiust.com" },
                      { icon: "ri-map-pin-line", label: "办公地址", value: "北京市朝阳区望京SOHO T3-A 2501" },
                      { icon: "ri-time-line", label: "工作时间", value: "周一至周五 9:00-18:00" },
                    ].map((item) => (
                      <div key={item.label} className="flex items-start gap-4">
                        <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-primary-100 text-primary-600 flex-shrink-0">
                          <i className={item.icon + " text-lg"}></i>
                        </div>
                        <div>
                          <p className="text-xs text-foreground-500">{item.label}</p>
                          <p className="text-sm font-medium text-foreground-900 mt-0.5">{item.value}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="bg-white rounded-2xl p-6 md:p-8 border border-background-200">
                  <h3 className="text-lg font-semibold text-foreground-950 mb-5">
                    商务合作咨询
                  </h3>
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-medium text-foreground-700 mb-1.5">
                          姓名 *
                        </label>
                        <input
                          type="text"
                          name="name"
                          required
                          value={formData.name}
                          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                          className="w-full px-3 py-2.5 rounded-lg border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-300"
                          placeholder="请输入姓名"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-foreground-700 mb-1.5">
                          邮箱 *
                        </label>
                        <input
                          type="email"
                          name="email"
                          required
                          value={formData.email}
                          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                          className="w-full px-3 py-2.5 rounded-lg border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-300"
                          placeholder="请输入邮箱"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-foreground-700 mb-1.5">
                        咨询主题 *
                      </label>
                      <select
                        name="subject"
                        required
                        value={formData.subject}
                        onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                        className="w-full px-3 py-2.5 rounded-lg border border-background-200 text-sm text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-300 bg-white"
                      >
                        <option value="">请选择咨询主题</option>
                        <option value="business">商务合作</option>
                        <option value="copyright">版权授权</option>
                        <option value="ads">广告投放</option>
                        <option value="feedback">用户反馈</option>
                        <option value="other">其他</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-foreground-700 mb-1.5">
                        详细说明 *
                      </label>
                      <textarea
                        name="message"
                        required
                        maxLength={500}
                        value={formData.message}
                        onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                        rows={4}
                        className="w-full px-3 py-2.5 rounded-lg border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-300 resize-none"
                        placeholder="请详细描述您的需求或问题"
                      />
                      <p className="mt-1 text-xs text-foreground-400 text-right">{formData.message.length}/500</p>
                    </div>
                    <input
                      name="website_alt"
                      type="text"
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                      readOnly
                      className="absolute opacity-0 pointer-events-none"
                      style={{ position: "absolute", left: "-9999px", top: "-9999px" }}
                    />
                    <button
                      type="submit"
                      className="w-full px-6 py-3 rounded-full bg-primary-500 text-white text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      提交咨询
                    </button>
                    {formMsg && (
                      <p className={`mt-3 text-sm text-center ${formStatus === "success" ? "text-green-600" : "text-red-600"}`}>
                        {formMsg}
                      </p>
                    )}
                  </form>
                </div>
              </div>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}